// ============================================================================
// CrewAhead Maritime Platform - Matching API Functions
// ============================================================================
// Job-to-candidate and candidate-to-job matching engine.
// All endpoints return match scores computed by the backend matching service.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type { MatchedJob, MatchedCandidate, MatchScoreDetail } from '../types';

// ─── Candidate-Facing: Matched Jobs ──────────────────────────────────────────

/**
 * GET /matching/jobs/
 *
 * Returns jobs ranked by match score for the currently authenticated
 * candidate (seafarer or shore). Results are ordered by overallScore
 * descending (best matches first).
 *
 * @param params - Optional filters and pagination
 * @returns Paginated matched jobs with score breakdowns
 */
export async function getMatchedJobs(params?: {
  minScore?: number;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  results: MatchedJob[];
}> {
  try {
    const query = new URLSearchParams();

    if (params?.minScore !== undefined) {
      query.set('min_score', String(params.minScore));
    }
    if (params?.page !== undefined) {
      query.set('page', String(params.page));
    }
    if (params?.pageSize !== undefined) {
      query.set('page_size', String(params.pageSize));
    }

    const qs = query.toString();
    const url = `${API_ENDPOINTS.MATCHING.JOBS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
      results: MatchedJob[];
    }>(url);

    return {
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.total_pages,
      results: response.data.results,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Candidate-Facing: Single Job Match Score ────────────────────────────────

/**
 * GET /matching/score/?job_id={jobId}
 *
 * Returns a detailed match score breakdown for a specific job
 * against the current candidate's profile.
 *
 * @param jobId - The job posting ID to score against
 * @returns Detailed match score with strengths and gaps
 */
export async function getMatchScore(jobId: string): Promise<MatchScoreDetail> {
  try {
    const response = await apiClient.get<MatchScoreDetail>(
      `${API_ENDPOINTS.MATCHING.SCORE}?job_id=${encodeURIComponent(jobId)}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Employer-Facing: Matched Candidates ─────────────────────────────────────

/**
 * GET /matching/candidates/?job_id={jobId}&min_score={minScore}
 *
 * Employer-only endpoint. Returns candidates with active availability
 * posts that match the specified job, ordered by match score.
 *
 * @param jobId - The job posting ID to match candidates against
 * @param params - Optional score threshold and pagination
 * @returns Paginated matched candidates with score breakdowns
 */
export async function getMatchedCandidates(
  jobId: string,
  params?: { minScore?: number; page?: number; pageSize?: number }
): Promise<{
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  results: MatchedCandidate[];
}> {
  try {
    const query = new URLSearchParams();

    query.set('job_id', jobId);

    if (params?.minScore !== undefined) {
      query.set('min_score', String(params.minScore));
    }
    if (params?.page !== undefined) {
      query.set('page', String(params.page));
    }
    if (params?.pageSize !== undefined) {
      query.set('page_size', String(params.pageSize));
    }

    const response = await apiClient.get<{
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
      results: MatchedCandidate[];
    }>(`${API_ENDPOINTS.MATCHING.CANDIDATES}?${query.toString()}`);

    return {
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.total_pages,
      results: response.data.results,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Admin: Trigger Recalculation ────────────────────────────────────────────

/**
 * POST /matching/recalculate/
 *
 * Admin-only endpoint. Triggers a batch recalculation of match scores.
 * Optionally scoped to a single job or candidate.
 *
 * @param params - Optional scope filters
 * @returns Number of records processed and errors encountered
 */
export async function triggerMatchRecalculate(params?: {
  jobId?: string;
  candidateId?: string;
}): Promise<{ processed: number; errors: number }> {
  try {
    const payload: Record<string, unknown> = {};

    if (params?.jobId) {
      payload.job_id = params.jobId;
    }
    if (params?.candidateId) {
      payload.candidate_id = params.candidateId;
    }

    const response = await apiClient.post<{
      processed: number;
      errors: number;
    }>(API_ENDPOINTS.MATCHING.RECALCULATE, payload);

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
